import { useState } from 'react';
import { ExternalLink, CheckCircle2 } from 'lucide-react';
import { useMcpAuthEvents } from '../../hooks/useMcpAuthEvents';
import type { McpAuthRequiredResult } from '../../types/mcp';

interface McpAuthCalloutProps {
  detail: McpAuthRequiredResult;
  /** Compact (chain-of-thought) vs full (Thread tool UI) styling. */
  compact?: boolean;
}

/**
 * Inline call-to-action rendered inside a `ToolCallCard` when the tool
 * result signals that the caller hasn't completed OAuth consent with the
 * referenced MCP server. Opens the authorization URL in a new tab and
 * flips to a "connected — retry" state when the backend confirms.
 */
export function McpAuthCallout({ detail, compact }: McpAuthCalloutProps) {
  const [connected, setConnected] = useState(false);

  useMcpAuthEvents({
    serverId: detail.serverId,
    onConnected: () => setConnected(true),
  });

  const openAuthUrl = () => {
    window.open(detail.authorizationUrl, '_blank', 'noopener,noreferrer');
  };

  const headingClass = compact
    ? 'text-xs font-medium text-foreground'
    : 'text-sm font-medium text-foreground';
  const bodyClass = compact
    ? 'text-xs text-muted-foreground'
    : 'text-sm text-muted-foreground';

  return (
    <div
      className={`rounded-md border ${
        connected
          ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
          : 'border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20'
      } ${compact ? 'p-2.5' : 'p-3'} space-y-2`}
    >
      <div className={headingClass}>
        {connected
          ? `Connected to ${detail.serverName}`
          : `Connect ${detail.serverName} to continue`}
      </div>
      <p className={bodyClass}>
        {connected
          ? 'Re-send your last message and the agent will retry with your new connection.'
          : (detail.hint ??
            'This MCP server needs your authorization before the agent can use it. The consent page opens in a new tab.')}
      </p>
      {!connected && (
        <button
          type="button"
          onClick={openAuthUrl}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-accent"
        >
          <ExternalLink className="h-3 w-3" />
          Connect {detail.serverName}
        </button>
      )}
      {connected && (
        <div className="inline-flex items-center gap-1.5 text-xs text-green-700 dark:text-green-300">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Connection ready
        </div>
      )}
    </div>
  );
}
